'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import VerificationBadges from './VerificationBadges';
import ClockStatusCard from './ClockStatusCard';
import ClockActionButton from './ClockActionButton';
import TodaySessionsSummary from './TodaySessionsSummary';
import ExpiredQRState from './ExpiredQRState';
import ClockSuccessState from './ClockSuccessState';

type PageState = 'verifying' | 'expired' | 'ready' | 'success' | 'error';
type ClockStatus = 'clocked_in' | 'clocked_out';

interface Employee {
  id: string;
  name: string;
  role: string;
  avatarInitials: string;
}

interface SessionEntry {
  id: string;
  clockIn: string;
  clockOut: string | null;
  duration: string | null;
}

interface VerificationStepProps {
  label: string;
  done: boolean;
  active: boolean;
}

function VerificationStep({ label, done, active }: VerificationStepProps) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
        done ? 'bg-primary' : active ? 'border-2 border-primary' : 'border-2 border-border'
      }`}>
        {done ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ) : active ? (
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        ) : null}
      </div>
      <span className={`text-sm ${done ? 'text-foreground font-medium' : active ? 'text-foreground' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  );
}

export default function ClockActionPage() {
  const searchParams = useSearchParams();
  // Token comes from the URL path — passed as a prop from the page component
  const [qrToken, setQrToken] = useState<string>('');

  const [pageState, setPageState] = useState<PageState>('verifying');
  const [expiredMessage, setExpiredMessage] = useState<string>('');
  const [clockStatus, setClockStatus] = useState<ClockStatus>('clocked_out');
  const [recordedTime, setRecordedTime] = useState<string>('');
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [qrVerified, setQrVerified] = useState<boolean>(false);
  const [verifyingStep, setVerifyingStep] = useState<number>(0);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [lastAction, setLastAction] = useState<'clock_in' | 'clock_out'>('clock_in');

  // Display clock — uses local time for display only, NOT for official punches
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours() % 12 || 12;
      const m = String(now.getMinutes()).padStart(2, '0');
      const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
      setCurrentTime(`${h}:${m} ${ampm}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  // Extract token from URL — /q/[token]
  useEffect(() => {
    // Token is in the URL path: /q/<token>
    const pathParts = window.location.pathname.split('/');
    const tokenFromPath = pathParts[pathParts.length - 1];
    if (tokenFromPath && tokenFromPath !== 'q') {
      setQrToken(tokenFromPath);
    }
  }, [searchParams]);

  const runVerification = useCallback(async (token: string) => {
    if (!token) return;
    setPageState('verifying');
    setVerifyingStep(0);

    try {
      // Step 1: Validate QR token with server
      setVerifyingStep(0);
      const qrRes = await fetch('/api/qr/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        credentials: 'include',
      });
      const qrData = await qrRes.json();

      if (!qrData.valid) {
        if (qrData.reason === 'expired') {
          setExpiredMessage(qrData.message ?? 'This QR code has expired. Please scan the current QR displayed at The English Island.');
          setPageState('expired');
          return;
        }
        setExpiredMessage(qrData.message ?? 'This QR code is not valid.');
        setPageState('expired');
        return;
      }

      setVerifyingStep(1);
      setQrVerified(true);

      // Step 2: Load employee status from server session
      setVerifyingStep(1);
      const statusRes = await fetch('/api/me/status', { credentials: 'include' });

      if (!statusRes.ok) {
        if (statusRes.status === 401) {
          // Not authenticated — redirect to login
          window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
          return;
        }
        setErrorMessage('Failed to load your account status. Please try again.');
        setPageState('error');
        return;
      }

      const statusData = await statusRes.json();
      setVerifyingStep(2);

      setEmployee({
        id: statusData.employeeId ?? '',
        name: statusData.fullName ?? 'Employee',
        role: statusData.role ?? '',
        avatarInitials: getInitials(statusData.fullName ?? 'E'),
      });

      setClockStatus(statusData.isClockedIn ? 'clocked_in' : 'clocked_out');
      setSessions(statusData.todaySessions ?? []);
      setPageState('ready');
    } catch {
      setErrorMessage('Network error. Please check your connection and try again.');
      setPageState('error');
    }
  }, []);

  useEffect(() => {
    if (qrToken) {
      runVerification(qrToken);
    }
  }, [qrToken, runVerification]);

  const handleClockAction = async () => {
    if (!qrToken || !employee) return;
    setIsLoading(true);
    setErrorMessage('');

    try {
      const action = clockStatus === 'clocked_out' ? 'clock_in' : 'clock_out';

      // POST to server — employee identity comes from server session, NOT from this request body
      const res = await fetch('/api/clock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, qrToken }),
        credentials: 'include',
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          // Duplicate clock action
          setErrorMessage(data.error ?? 'Clock action conflict. Please refresh and try again.');
          setPageState('error');
          return;
        }
        if (res.status === 400 && data.error?.includes('expired')) {
          setExpiredMessage(data.error);
          setPageState('expired');
          return;
        }
        setErrorMessage(data.error ?? 'Clock action failed. Please try again.');
        setPageState('error');
        return;
      }

      // Success — use server-returned time, not browser time
      setRecordedTime(data.displayTime);
      setLastAction(action);

      if (action === 'clock_in') {
        setClockStatus('clocked_in');
        setSessions((prev) => [
          ...prev,
          { id: data.sessionId, clockIn: data.displayTime, clockOut: null, duration: null },
        ]);
      } else {
        setClockStatus('clocked_out');
        setSessions((prev) =>
          prev.map((s, i) =>
            i === prev.length - 1 && s.clockOut === null
              ? { ...s, clockOut: data.displayTime, duration: 'Calculating...' }
              : s
          )
        );
      }

      setPageState('success');
    } catch {
      setErrorMessage('Network error. Please check your connection and try again.');
      setPageState('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #f0f5ff 0%, #fafcff 60%, #f5f8ff 100%)' }}>
      {/* Minimal phone header */}
      <header className="flex items-center justify-between px-5 py-4 border-b border-border bg-card">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span className="font-semibold text-foreground text-base">TEI Staff &amp; Faculty Time Clock</span>
        </div>
        <button
          onClick={async () => {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
            window.location.href = '/login';
          }}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          Sign out
        </button>
      </header>

      <main className="max-w-sm mx-auto px-5 py-6 flex flex-col gap-5">
        {pageState === 'verifying' && (
          <div className="fade-in flex flex-col gap-5">
            <div className="card-phone p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-base">Verifying QR code...</p>
                  <p className="text-sm text-muted-foreground">Checking with server</p>
                </div>
              </div>
              <div className="flex flex-col gap-2.5">
                <VerificationStep label="QR token validation" done={verifyingStep >= 1} active={verifyingStep === 0} />
                <VerificationStep label="Loading your status" done={verifyingStep >= 2} active={verifyingStep === 1} />
                <VerificationStep label="Ready" done={verifyingStep >= 3} active={verifyingStep === 2} />
              </div>
            </div>
          </div>
        )}

        {pageState === 'expired' && <ExpiredQRState message={expiredMessage} />}

        {pageState === 'ready' && employee && (
          <div className="fade-in flex flex-col gap-5">
            {/* Employee greeting */}
            <div className="card-phone p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-base">{employee.avatarInitials}</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Hello,</p>
                  <p className="font-bold text-foreground text-xl leading-tight">{employee.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{employee.role}</p>
                </div>
              </div>
            </div>

            {/* Verification badges — only shown when server confirmed */}
            <VerificationBadges qrVerified={qrVerified} onSiteVerified={false} />

            {/* Clock status */}
            <ClockStatusCard status={clockStatus} currentTime={currentTime} />

            {/* Action button */}
            <ClockActionButton status={clockStatus} isLoading={isLoading} onAction={handleClockAction} />

            {/* Today's sessions */}
            {sessions.length > 0 && <TodaySessionsSummary sessions={sessions} />}
          </div>
        )}

        {pageState === 'success' && employee && (
          <ClockSuccessState
            employee={employee}
            action={lastAction}
            recordedTime={recordedTime}
            sessions={sessions}
            onDone={() => setPageState('ready')}
          />
        )}

        {pageState === 'error' && (
          <div className="card-phone p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <p className="font-semibold text-foreground mb-1">Clock action failed</p>
            <p className="text-sm text-muted-foreground mb-4">
              {errorMessage || 'Could not record your punch. Please try scanning the QR again.'}
            </p>
            <button
              onClick={() => { if (qrToken) runVerification(qrToken); }}
              className="text-primary font-medium text-sm hover:underline"
            >
              Try again
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}
